/*
 * Landing page copy and figures.
 *
 * ⚠️  EVERY NUMBER IN THIS FILE IS PLACEHOLDER MARKETING COPY taken from the
 * design prototype, where it was explicitly generated as fake data. Volumes,
 * open interest, slippage, fill rates, latency, TVL, APRs and fee tiers have
 * NOT been verified against the protocol.
 *
 * Publishing unverified performance claims on a financial product is a legal
 * and reputational risk. Replace each figure with a real feed or a
 * substantiated number before this page goes live.
 */

export type HeroHeadline = { lead: string; accent: string };
export type ChartSymbol = { label: string; symbol: string };
export type Venue = {
  key: string;
  name: string;
  mark: string;
  markBg: string;
  markInk: string;
  kind: string;
  chain: string;
  body: string;
  tags: string[];
};
export type RouteQuote = { venue: string; entry: string; fee: string; impact: string; net: string; best: boolean };
export type HeroStat = { label: string; value: string; meta: string };
export type TapeSymbol = { proName: string; title: string };
export type BentoMetric = { label: string; value: string; note: string };
export type BentoCard = { eyebrow: string; title: string; body: string; stat: string; statNote: string };
export type Step = { n: string; title: string; body: string };
export type FeeTier = { tier: string; volume: string; maker: string; taker: string; leverage: string };
export type Pool = { short: string; name: string; desc: string; apr: string; tvl: string; epoch: string; mark: string };
export type Faq = { q: string; a: string };

/** Where "Launch App" and every in-app CTA point. */
export const APP_ENTRY_ROUTE = "/";

/**
 * Hero headlines, cycled by `HeroSection`.
 *
 * `lead` renders in the base ink, `accent` in the brand gradient. Keep each one
 * to two short clauses — they share a single grid cell, so the hero reserves
 * the height of the tallest and a long third line would pad the others out.
 */
export const HERO_HEADLINES: HeroHeadline[] = [
  { lead: "The price you see is", accent: "the price you fill." },
  { lead: "One order, every venue —", accent: "routed to the best fill." },
  { lead: "Deep liquidity on-chain,", accent: "settled in under a second." },
];

/** How long each hero headline holds before the next fades in. */
export const HERO_HEADLINE_INTERVAL_MS = 8000;

/**
 * Markets offered in the landing chart's symbol switcher.
 *
 * These are TradingView symbols for the underlying spot pairs — the widget is
 * reference price context for visitors, not the venue's own perp feed.
 */
export const CHART_SYMBOLS: ChartSymbol[] = [
  { label: "BTC", symbol: "BINANCE:BTCUSDT" },
  { label: "ETH", symbol: "BINANCE:ETHUSDT" },
  { label: "SOL", symbol: "BINANCE:SOLUSDT" },
  { label: "AVAX", symbol: "BINANCE:AVAXUSDT" },
  { label: "ARB", symbol: "BINANCE:ARBUSDT" },
];

/** The venues USDX routes into. Descriptions are mechanism, not performance. */
export const VENUES: Venue[] = [
  {
    key: "gmx",
    name: "GMX",
    mark: "G",
    markBg: "#2D42FC",
    markInk: "#ffffff",
    kind: "Pool-based perps",
    chain: "Arbitrum · Avalanche",
    body:
      "Oracle-priced execution against GM and GLV pools. There is no book to walk, so a large clip fills at the oracle mark with a price impact that is knowable before you sign.",
    tags: ["Zero spread", "Oracle mark", "Size without slippage"],
  },
  {
    key: "hyperliquid",
    name: "Hyperliquid",
    mark: "H",
    markBg: "#97FCE4",
    markInk: "#04211a",
    kind: "On-chain order book",
    chain: "HyperCore L1",
    body:
      "A fully on-chain central limit order book on sub-second blocks. Tight top-of-book on majors and maker rebates make it the cheaper side for small and mid-size clips.",
    tags: ["Tight top-of-book", "Sub-second blocks", "Maker rebates"],
  },
];

/**
 * Illustrative routing decision for the Routing section.
 *
 * ⚠️  Like every figure in this file, these quotes are made up — they show the
 * shape of the comparison, not a real quote from either venue. Wire the section
 * to the live router before publishing.
 */
export const ROUTE_EXAMPLE = {
  order: "Long 4.2 BTC-USD · $500,000 notional",
  quotes: [
    { venue: "GMX", entry: "$118,420.50", fee: "$225.00", impact: "0.0 bps", net: "$118,425.31", best: true },
    { venue: "Hyperliquid", entry: "$118,431.00", fee: "$175.00", impact: "1.9 bps", net: "$118,455.47", best: false },
  ] as RouteQuote[],
  verdict: "Routed to GMX — $126.65 cheaper on net entry at this size.",
};

export const HERO_STATS: HeroStat[] = [
  { label: "24h Volume", value: "$1.42B", meta: "Across 47 markets" },
  { label: "Open Interest", value: "$242M", meta: "Globally, all pairs" },
  { label: "Avg. Slippage", value: "1.6 bps", meta: "Industry avg. 8 bps" },
  { label: "Order-to-Fill", value: "0.7 ms", meta: "p50, co-located" },
];

/**
 * Markets shown in the live marquee.
 *
 * `proName` is the TradingView symbol the tape quotes; `title` is the label
 * USDX shows for that market.
 */
export const TAPE_SYMBOLS: TapeSymbol[] = [
  { proName: "BINANCE:BTCUSDT", title: "BTC-USD" },
  { proName: "BINANCE:ETHUSDT", title: "ETH-USD" },
  { proName: "BINANCE:SOLUSDT", title: "SOL-USD" },
  { proName: "BINANCE:AVAXUSDT", title: "AVAX-USD" },
  { proName: "BINANCE:ARBUSDT", title: "ARB-USD" },
  { proName: "BINANCE:LINKUSDT", title: "LINK-USD" },
  { proName: "BINANCE:OPUSDT", title: "OP-USD" },
];

export const TERMINAL_META = [
  { k: "24h High", v: "$121,148" },
  { k: "24h Low", v: "$112,192" },
  { k: "Funding", v: "+0.0084%" },
  { k: "Open Int.", v: "$88.4M" },
];

export const BENTO_METRICS: BentoMetric[] = [
  { label: "Bid-ask spread", value: "0.8 bps", note: "Industry avg. 5 bps" },
  { label: "Order book depth", value: "$37.1M", note: "Top five levels" },
  { label: "Fill at quoted price", value: "99.2%", note: "Last 30 days" },
];

export const BENTO_CARDS: BentoCard[] = [
  {
    eyebrow: "02 — Low slippage",
    title: "Seal the slippage leak",
    body: "AI-driven smart order routing with dynamic fragmentation, so your size never signals itself to the market before it fills.",
    stat: "1.6 bps",
    statNote: "avg. realised slippage",
  },
  {
    eyebrow: "03 — Fast execution",
    title: "Close the latency window",
    body: "Co-located matching engines in Tier-1 data centres. FPGA-assisted order processing with WebSocket and FIX support.",
    stat: "0.7 ms",
    statNote: "order-to-fill, p50",
  },
  {
    eyebrow: "04 — Oracles",
    title: "No wick liquidations",
    body: "Sub-second, multi-source medianised price feeds. A single venue's spike cannot move your liquidation price.",
    stat: "400 ms",
    statNote: "oracle refresh",
  },
  {
    eyebrow: "05 — Custody",
    title: "Your keys, your margin",
    body: "Collateral sits in an audited vault contract you can exit at any block. No deposits held by an operator.",
    stat: "100%",
    statNote: "self-custodial",
  },
];

export const STEPS: Step[] = [
  {
    n: "01",
    title: "Connect your wallet",
    body: "Any EVM wallet works. No account, no email, no KYC for self-custodial trading — you are quoted the moment you connect.",
  },
  {
    n: "02",
    title: "Deposit collateral",
    body: "Bridge or deposit USDC into an audited vault contract. Cross-collateral netting applies instantly across every open position.",
  },
  {
    n: "03",
    title: "Trade at the quote",
    body: "Set size and leverage, review the committed fill price, and submit. The router either fills at the quote or rejects — never worse.",
  },
];

export const FEE_TIERS: FeeTier[] = [
  { tier: "Base", volume: "< $1M", maker: "+0.20 bps", taker: "4.5 bps", leverage: "20x" },
  { tier: "Tier 1", volume: "$1M – $10M", maker: "+0.35 bps", taker: "3.6 bps", leverage: "25x" },
  { tier: "Tier 2", volume: "$10M – $50M", maker: "+0.50 bps", taker: "2.9 bps", leverage: "35x" },
  { tier: "Tier 3", volume: "$50M – $250M", maker: "+0.65 bps", taker: "2.3 bps", leverage: "50x" },
  { tier: "Market maker", volume: "> $250M", maker: "+0.80 bps", taker: "1.8 bps", leverage: "50x" },
];

export const POOLS: Pool[] = [
  {
    short: "XLP",
    name: "USDX-LP",
    desc: "Broad market exposure",
    apr: "18.4%",
    tvl: "$186M",
    epoch: "6d 14h",
    mark: "linear-gradient(150deg,#4392F7,#0A5AA8)",
  },
  {
    short: "SXD",
    name: "sUSDX",
    desc: "Stable, delta-neutral",
    apr: "9.7%",
    tvl: "$104M",
    epoch: "6d 14h",
    mark: "linear-gradient(150deg,#2BC48D,#0E9F6E)",
  },
  {
    short: "VLT",
    name: "Maker Vault",
    desc: "Depth provision, higher risk",
    apr: "31.2%",
    tvl: "$50M",
    epoch: "6d 14h",
    mark: "linear-gradient(150deg,#A376E9,#6D3FC4)",
  },
];

export const FAQS: Faq[] = [
  {
    q: "Is USDX a decentralized exchange or a centralized platform?",
    a: "Non-custodial and permissionless. Positions settle on-chain against smart contracts you can audit; USDX Labs never takes custody of collateral and cannot freeze or seize a position.",
  },
  {
    q: "How do you deliver zero slippage on large size?",
    a: "Orders route across aggregated venue depth with a toxicity score applied per flow. The quote you are shown is the price the router has already committed depth against — if it cannot be filled at that price, the order is rejected rather than filled worse.",
  },
  {
    q: "What is the maximum leverage available?",
    a: "Up to 50x on majors and 20x on long-tail markets, scaled by your volume tier. Maintenance margin is calculated per-position with cross-collateral netting across your account.",
  },
  {
    q: "What are the trading fees?",
    a: "Taker fees start at 4.5 bps and fall to 1.8 bps at the top tier. Makers earn a rebate of up to 0.8 bps, paid in USDC at every epoch close. There is no spread markup layered on top.",
  },
  {
    q: "Is my capital safe?",
    a: "Contracts are audited by three independent firms with a continuous bug-bounty programme. An insurance fund backstops socialised losses, and oracle feeds are sub-second with multi-source medianisation to prevent wick liquidations.",
  },
  {
    q: "How do I get started?",
    a: "Connect any EVM wallet, deposit USDC, and open a position. No account, no KYC for self-custodial trading, no email required.",
  },
];

export const FOOTER_COLUMNS = [
  { title: "Product", links: ["Trade", "Markets", "Earn", "Referrals"] },
  { title: "Learn", links: ["Docs", "Fee structure", "Risk guide", "API"] },
  { title: "Company", links: ["About", "Careers", "Blog", "Brand kit"] },
  { title: "Legal", links: ["Terms", "Privacy", "Disclosures", "Audits"] },
];

export const NAV_LINKS = [
  { label: "Markets", href: "#markets" },
  { label: "Trade", href: "#terminal" },
  { label: "Earn", href: "#earn" },
  { label: "Fees", href: "#fees" },
  { label: "Docs", href: "#faq" },
];
